/**
 * BackgroundSelector Component
 *
 * Scalable UI for selecting video background type and palette.
 * Designed to easily accommodate new background types in the future.
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Check, Sparkles, Waves, Zap, Grid3X3, Circle, X } from 'lucide-react';

// Background type definitions - easily extensible
export const BACKGROUND_TYPES = {
  none: {
    id: 'none',
    name: 'None',
    description: 'Simple gradient background',
    icon: X,
    tags: ['minimal', 'clean', 'simple'],
    supportsPalette: false,
    paletteType: null,
  },
  aurora: {
    id: 'aurora',
    name: 'Aurora',
    description: 'Subtle northern lights effect',
    icon: Sparkles,
    tags: ['premium', 'luxury', 'elegant'],
    supportsPalette: false,
    paletteType: null,
  },
  animated_gradient: {
    id: 'animated_gradient',
    name: 'Animated Gradient',
    description: 'Dynamic rotating gradients',
    icon: Waves,
    tags: ['modern', 'energetic', 'playful'],
    supportsPalette: true,
    paletteType: 'gradient',
  },
  retro_grid: {
    id: 'retro_grid',
    name: 'Retro Grid',
    description: '80s synthwave perspective grid',
    icon: Grid3X3,
    tags: ['retro', 'gaming', 'tech'],
    supportsPalette: true,
    paletteType: 'retro',
  },
  ripple: {
    id: 'ripple',
    name: 'Ripple',
    description: 'Expanding dot grid effect',
    icon: Circle,
    tags: ['tech', 'modern', 'dynamic'],
    supportsPalette: false,
    paletteType: null,
  },
};

// Palette definitions with preview colors (for animated_gradient)
export const GRADIENT_PALETTES = {
  sunset: { name: 'Sunset', colors: ['#FF6B6B', '#FFA07A', '#FFD93D'], tags: ['warm', 'lifestyle'] },
  aqua: { name: 'Aqua', colors: ['#00D9FF', '#00B4D8', '#0077B6'], tags: ['cool', 'tech'] },
  aurora: { name: 'Aurora', colors: ['#00FF87', '#60EFFF', '#7B68EE'], tags: ['nature', 'fresh'] },
  candy: { name: 'Candy', colors: ['#FF6FD8', '#FF9A8B', '#FFECD2'], tags: ['playful', 'youth'] },
  forest: { name: 'Forest', colors: ['#134E5E', '#71B280', '#2ECC71'], tags: ['eco', 'natural'] },
  ocean: { name: 'Ocean', colors: ['#1A2980', '#26D0CE', '#00CED1'], tags: ['calm', 'premium'] },
  peach: { name: 'Peach', colors: ['#FFAB91', '#FF8A65', '#FF7043'], tags: ['warm', 'soft'] },
  lemon: { name: 'Lemon', colors: ['#F9D423', '#FF4E50', '#F7971E'], tags: ['energetic', 'bright'] },
  rose: { name: 'Rose', colors: ['#FF6B6B', '#FFC0CB', '#FFB6C1'], tags: ['romantic', 'feminine'] },
  sky: { name: 'Sky', colors: ['#87CEEB', '#00BFFF', '#1E90FF'], tags: ['clean', 'fresh'] },
  lava: { name: 'Lava', colors: ['#FF4500', '#FF6347', '#DC143C'], tags: ['intense', 'urgent'] },
  mint: { name: 'Mint', colors: ['#98FF98', '#00FA9A', '#3CB371'], tags: ['health', 'eco'] },
  grape: { name: 'Grape', colors: ['#8E44AD', '#9B59B6', '#663399'], tags: ['luxury', 'creative'] },
  sand: { name: 'Sand', colors: ['#C2B280', '#DEB887', '#D2691E'], tags: ['natural', 'warm'] },
  luxury: { name: 'Luxury', colors: ['#D4AF37', '#B8860B', '#FFD700'], tags: ['premium', 'gold'] },
  midnight: { name: 'Midnight', colors: ['#191970', '#000080', '#483D8B'], tags: ['dark', 'elegant'] },
  neon: { name: 'Neon', colors: ['#FF00FF', '#00FFFF', '#FFFF00'], tags: ['gaming', 'tech'] },
};

// Retro Grid presets (for retro_grid background)
export const RETRO_GRID_PRESETS = {
  synthwave: { name: 'Synthwave', colors: ['#ff00ff', '#0a0a1a', '#ff6b00'], tags: ['retro', '80s'] },
  cyberpunk: { name: 'Cyberpunk', colors: ['#00ffff', '#0d0221', '#ff2a6d'], tags: ['tech', 'neon'] },
  neon: { name: 'Neon', colors: ['#39ff14', '#0a0a0a', '#ff073a'], tags: ['gaming', 'bright'] },
  vaporwave: { name: 'Vaporwave', colors: ['#ff71ce', '#01cdfe', '#05ffa1'], tags: ['aesthetic', 'pastel'] },
  midnight: { name: 'Midnight', colors: ['#4a90d9', '#0c1445', '#7b2d8e'], tags: ['dark', 'elegant'] },
  sunset: { name: 'Sunset', colors: ['#ffb347', '#1a0a2e', '#ff6b6b'], tags: ['warm', 'retro'] },
  matrix: { name: 'Matrix', colors: ['#00ff00', '#000000', '#003300'], tags: ['tech', 'hacker'] },
  tron: { name: 'Tron', colors: ['#00d4ff', '#000814', '#ff6600'], tags: ['tech', 'futuristic'] },
};

// Palette preview component
const PalettePreview = ({ colors, size = 'sm' }) => {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8',
  };

  return (
    <div className="flex -space-x-1">
      {colors.map((color, idx) => (
        <div
          key={idx}
          className={`${sizeClasses[size]} rounded-full border-2 border-white shadow-sm`}
          style={{ backgroundColor: color }}
        />
      ))}
    </div>
  );
};

// Background type button
const BackgroundTypeButton = ({ type, isSelected, onClick }) => {
  const Icon = type.icon;

  return (
    <button
      onClick={onClick}
      className={`
        flex items-center gap-3 p-3 rounded-lg border-2 transition-all w-full text-left
        ${isSelected
          ? 'border-purple-500 bg-purple-50'
          : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50'
        }
      `}
    >
      <div className={`p-2 rounded-lg ${isSelected ? 'bg-purple-100' : 'bg-gray-100'}`}>
        <Icon className={`w-5 h-5 ${isSelected ? 'text-purple-600' : 'text-gray-500'}`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={`font-medium ${isSelected ? 'text-purple-900' : 'text-gray-900'}`}>
            {type.name}
          </span>
          {isSelected && <Check className="w-4 h-4 text-purple-500" />}
        </div>
        <p className="text-xs text-gray-500 truncate">{type.description}</p>
      </div>
    </button>
  );
};

// Palette grid selector
const PaletteGrid = ({ selectedPalette, onSelect, paletteType = 'gradient' }) => {
  // Select the correct palette set based on type
  const palettes = paletteType === 'retro' ? RETRO_GRID_PRESETS : GRADIENT_PALETTES;

  return (
    <div className="grid grid-cols-4 gap-2 mt-3">
      {Object.entries(palettes).map(([id, palette]) => (
        <button
          key={id}
          onClick={() => onSelect(id)}
          className={`
            flex flex-col items-center gap-1 p-2 rounded-lg border transition-all
            ${selectedPalette === id
              ? 'border-purple-500 bg-purple-50'
              : 'border-gray-200 hover:border-purple-300'
            }
          `}
          title={palette.name}
        >
          <PalettePreview colors={palette.colors} size="sm" />
          <span className="text-xs text-gray-600 truncate w-full text-center">
            {palette.name}
          </span>
        </button>
      ))}
    </div>
  );
};

/**
 * Main BackgroundSelector Component
 */
export const BackgroundSelector = ({
  backgroundType,
  backgroundPalette,
  onBackgroundTypeChange,
  onBackgroundPaletteChange,
  llmSuggested,
  compact = false,
}) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(!compact);

  const selectedType = BACKGROUND_TYPES[backgroundType] || BACKGROUND_TYPES.aurora;

  // Get the correct palette data based on background type
  const getPaletteData = () => {
    if (selectedType.paletteType === 'retro') {
      return RETRO_GRID_PRESETS[backgroundPalette] || RETRO_GRID_PRESETS.synthwave;
    }
    return GRADIENT_PALETTES[backgroundPalette] || GRADIENT_PALETTES.aurora;
  };
  const selectedPaletteData = getPaletteData();

  // Compact mode - just shows current selection with expand button
  if (compact && !isExpanded) {
    return (
      <button
        onClick={() => setIsExpanded(true)}
        className="flex items-center justify-between w-full p-3 rounded-lg border border-gray-200 hover:border-purple-300 transition-all"
      >
        <div className="flex items-center gap-3">
          <selectedType.icon className="w-5 h-5 text-purple-600" />
          <div className="text-left">
            <span className="font-medium text-gray-900">{selectedType.name}</span>
            {selectedType.supportsPalette && (
              <span className="text-gray-500 text-sm ml-2">• {selectedPaletteData.name}</span>
            )}
          </div>
        </div>
        <ChevronDown className="w-5 h-5 text-gray-400" />
      </button>
    );
  }

  return (
    <div className="space-y-3">
      {/* Header with collapse button (in compact mode) */}
      {compact && (
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-gray-700">
            {t('modal.product.backgroundType', { defaultValue: 'Background' })}
          </span>
          <button
            onClick={() => setIsExpanded(false)}
            className="text-xs text-purple-600 hover:text-purple-800"
          >
            {t('common.collapse', { defaultValue: 'Collapse' })}
          </button>
        </div>
      )}

      {/* Background Type Selection */}
      <div className="space-y-2">
        {Object.values(BACKGROUND_TYPES).map((type) => (
          <BackgroundTypeButton
            key={type.id}
            type={type}
            isSelected={backgroundType === type.id}
            onClick={() => {
              onBackgroundTypeChange(type.id);
              // Set appropriate default palette when switching background types
              if (type.paletteType === 'retro') {
                onBackgroundPaletteChange('synthwave');
              } else if (type.paletteType === 'gradient') {
                onBackgroundPaletteChange('aurora');
              }
            }}
          />
        ))}
      </div>

      {/* Palette Selection (only for types that support it) */}
      {selectedType.supportsPalette && (
        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">
              {selectedType.paletteType === 'retro'
                ? t('modal.product.gridStyle', { defaultValue: 'Grid Style' })
                : t('modal.product.colorPalette', { defaultValue: 'Color Palette' })
              }
            </span>
            {llmSuggested?.backgroundPalette && (
              <span className="text-xs text-purple-600">
                AI: {(selectedType.paletteType === 'retro'
                  ? RETRO_GRID_PRESETS[llmSuggested.backgroundPalette]?.name
                  : GRADIENT_PALETTES[llmSuggested.backgroundPalette]?.name
                )}
              </span>
            )}
          </div>
          <PaletteGrid
            selectedPalette={backgroundPalette}
            onSelect={onBackgroundPaletteChange}
            paletteType={selectedType.paletteType}
          />
        </div>
      )}

      {/* LLM Suggestion indicator */}
      {llmSuggested?.backgroundType && llmSuggested.backgroundType !== backgroundType && (
        <button
          onClick={() => {
            onBackgroundTypeChange(llmSuggested.backgroundType);
            if (llmSuggested.backgroundPalette) {
              onBackgroundPaletteChange(llmSuggested.backgroundPalette);
            }
          }}
          className="flex items-center gap-2 text-xs text-purple-600 hover:text-purple-800 mt-2"
        >
          <Zap className="w-3 h-3" />
          {t('modal.product.useAiSuggestion', { defaultValue: 'Use AI suggestion' })}:
          {BACKGROUND_TYPES[llmSuggested.backgroundType]?.name}
        </button>
      )}
    </div>
  );
};

export default BackgroundSelector;
